import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Plus, X } from "@phosphor-icons/react";

/**
 * ITLocationsPanel — admin-editable list of working locations that IT
 * users can pick from on the Team tab. Free-text tags, additions and
 * removals commit through the standard Settings dirty-bar (no per-
 * action save button).
 */
export function ITLocationsPanel({ settings, setSettings, isAdmin }) {
  const [draft, setDraft] = useState("");
  const items = settings.it_locations || [];

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    setSettings({ ...settings, it_locations: [...items, v] });
    setDraft("");
  };
  const remove = (v) => {
    setSettings({ ...settings, it_locations: items.filter((x) => x !== v) });
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white space-y-3" data-testid="it-locations-panel">
      <div className="flex items-center gap-2">
        <MapPin size={16} weight="duotone" className="text-[#4f4c90]" />
        <div className="font-heading font-semibold">IT working locations</div>
      </div>
      <div className="text-xs text-slate-500">
        Locations IT team members can pick from on the Team tab (e.g. Aberdeen, Glasgow, WFH).
      </div>
      <div className="flex flex-wrap gap-2">
        {items.length === 0 && (
          <div className="text-[11px] text-slate-500 italic">No locations yet — add one below.</div>
        )}
        {items.map((loc) => (
          <span key={loc} className="chip !text-slate-800 !border-slate-300 bg-slate-100 flex items-center gap-1" data-testid={`it-location-${loc}`}>
            {loc}
            {isAdmin && (
              <button type="button" onClick={() => remove(loc)} className="hover:text-red-500" title="Remove">
                <X size={11} weight="bold" />
              </button>
            )}
          </span>
        ))}
      </div>
      {isAdmin && (
        <div className="flex items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="Add location…"
            className="bg-white border-slate-200 h-9 max-w-xs"
            data-testid="it-location-input"
          />
          <Button onClick={add} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1" data-testid="it-location-add">
            <Plus size={12} weight="bold" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
